import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getGuildInfo } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { canNavigateDashboard } from './DashboardSaveBar';
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { DashboardBrand } from "./DashboardBrand";
import { parseAppPath } from "../lib/routes";
import { dashboardPageTitle } from "../lib/dashboardPageTitle";

const getGuildIconUrl = (guildId, iconHash) => {
  if (!guildId || !iconHash) return "";
  const isAnimated = String(iconHash).startsWith("a_");
  const ext = isAnimated ? "gif" : "png";
  return `https://cdn.discordapp.com/icons/${guildId}/${iconHash}.${ext}?size=64`;
};

const SYRIA_FLAG_URL = `${process.env.PUBLIC_URL || ""}/flags/syria-new.svg`;
const USA_FLAG_URL = `${process.env.PUBLIC_URL || ""}/flags/usa.svg`;

export const TopHeader = ({ sidebarLocked = false, sidebarCollapsed = false }) => {
  const { language, setLanguage, t } = useI18n();
  const location = useLocation();
  const pathContext = parseAppPath(location.pathname);
  const pageTitle = dashboardPageTitle(pathContext, t);
  const reduced = useReducedMotion();
  const [guildInfo, setGuildInfo] = useState(null);
  const [guildFallback, setGuildFallback] = useState(null);
  const didLoadRef = useRef(false);
  const guildInfoCacheRef = useRef(new Map());
  const activeGuildIdRef = useRef("");

  const loadGuildInfo = useCallback(async (guildId, fallback = null) => {
    const gid = (guildId || "").toString();
    activeGuildIdRef.current = gid;

    if (!gid) {
      setGuildInfo(null);
      return;
    }

    const cached = guildInfoCacheRef.current.get(gid);
    if (cached) {
      setGuildInfo(cached);
      return;
    }

    if (fallback?.name || fallback?.icon) {
      setGuildInfo((prev) => {
        if (
          prev?.id === gid &&
          prev?.name === (fallback?.name || "") &&
          prev?.icon === (fallback?.icon || "")
        ) {
          return prev;
        }
        return {
          id: gid,
          name: fallback?.name || "",
          icon: fallback?.icon || "",
        };
      });
    }

    try {
      const info = await getGuildInfo(gid);
      if (activeGuildIdRef.current !== gid) return;
      guildInfoCacheRef.current.set(gid, info);
      setGuildInfo(info);
    } catch (e) {
      if (activeGuildIdRef.current !== gid) return;
      if (!fallback) setGuildInfo(null);
    }
  }, []);

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    const load = async () => {
      const gid = (localStorage.getItem("tns:selected_guild_id") || "").toString();
      await loadGuildInfo(gid);
    };
    load();
  }, [loadGuildInfo]);

  useEffect(() => {
    const handler = async (event) => {
      const next = event?.detail || null;
      const gid = (next?.guild_id || "").toString();
      await loadGuildInfo(gid);
    };
    window.addEventListener("tns:settings-updated", handler);
    return () => window.removeEventListener("tns:settings-updated", handler);
  }, [loadGuildInfo]);

  useEffect(() => {
    const handler = async (event) => {
      const g = event?.detail?.guild || null;
      const gid = (g?.id || "").toString();
      const fallback = g ? { id: gid, name: g?.name || "", icon: g?.icon || "" } : null;
      setGuildFallback(fallback);
      await loadGuildInfo(gid, fallback);
    };
    window.addEventListener("tns:guild-selected", handler);
    return () => window.removeEventListener("tns:guild-selected", handler);
  }, [loadGuildInfo]);

  const guildName = guildInfo?.name || guildFallback?.name || "";
  const guildIcon =
    guildInfo?.icon
      ? getGuildIconUrl(guildInfo.id, guildInfo.icon)
      : guildFallback?.icon
        ? getGuildIconUrl(guildFallback.id, guildFallback.icon)
        : "";
  const hasGuild = Boolean(guildInfo?.id || guildFallback?.id);
  const serverInitials = useMemo(() => {
    const parts = (guildName || "").split(" ").filter(Boolean);
    return (parts[0]?.[0] || "S") + (parts[1]?.[0] || "");
  }, [guildName]);

  return (
    <header className="tns-top-header sticky top-0 z-30 border-b border-border bg-background/80">
      <div className="tns-header-inner h-16 px-4 sm:px-6 lg:px-8 flex items-center gap-4">
        <DashboardBrand className={`tns-header-home ${sidebarCollapsed || sidebarLocked ? '' : 'tns-header-home-with-sidebar'}`} />
        <div className="flex-1 flex items-center min-w-0">
            <motion.div key={pathContext.guildId || 'account'} className="tns-header-context flex items-center gap-3 min-w-0"
              initial={reduced ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}>
              {hasGuild && pathContext.scope === 'server' ? (
              <Avatar className="h-9 w-9 rounded-lg shrink-0 hidden sm:flex">
                <AvatarImage src={guildIcon || undefined} alt={guildName} />
                <AvatarFallback className="rounded-lg font-mono text-xs">{serverInitials}</AvatarFallback>
              </Avatar>
              ) : null}
              <div className="min-w-0">
                <p className="tns-header-eyebrow truncate">{pathContext.scope === 'server' ? guildName || t('الخادم', 'Server') : t('حسابك', 'Your Account')}</p>
                <p className="tns-header-page truncate" aria-current="page">{pageTitle}</p>
              </div>
            </motion.div>
        </div>
        <div className="w-[112px] sm:w-[152px] shrink-0 flex items-center justify-end">
          <div className="w-full max-w-[168px]">
            <Select value={language} onValueChange={(value) => { if (canNavigateDashboard()) setLanguage(value); }}>
              <SelectTrigger className="h-9" data-testid="dashboard-language-select">
                <SelectValue placeholder={t("لغة اللوحة", "Dashboard Language")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">
                  <span className="flex items-center gap-2">
                    <img src={SYRIA_FLAG_URL} alt="Syria" className="h-4 w-6 rounded-[2px] border border-white/15 object-cover" />
                    <span>{t("العربية", "Arabic")}</span>
                  </span>
                </SelectItem>
                <SelectItem value="en">
                  <span className="flex items-center gap-2">
                    <img src={USA_FLAG_URL} alt="United States" className="h-4 w-6 rounded-[2px] border border-white/15 object-cover" />
                    <span>{t("الإنجليزية", "English")}</span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </header>
  );
};
